import React from 'react';

import { fetchSiteArt } from '../creatives/above-the-fold-api';
import { siteArtImageUrls } from './share-image';

export type SiteArtLibraryStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface SiteArtLibrary {
  /** idle = no tenant to read for. */
  status: SiteArtLibraryStatus;
  /** Every image the brand's website art shows (as the site serves it; the picker decides which can be shared). */
  urls: string[];
  reload: () => void;
}

type Held = { tenantId: number | undefined; status: SiteArtLibraryStatus; urls: string[] };

/**
 * The brand's website art as a picker library. Read where it is used (it reads every deployment from
 * casino-creation): mount it only where the library shows. Every read carries an id from a growing counter and only
 * the newest one lands; a re-read that fails keeps the last good list; a tenant change disowns in-flight reads.
 */
export const useSiteArtLibrary = (tenantId?: number): SiteArtLibrary => {
  const [held, setHeld] = React.useState<Held>({
    tenantId,
    status: tenantId === undefined ? 'idle' : 'loading',
    urls: [],
  });
  const [nonce, setNonce] = React.useState(0);
  const readIdRef = React.useRef(0);

  // Syncs the list with casino-creation's site-art read for this tenant; aborts the read on a change or unmount.
  React.useEffect(() => {
    readIdRef.current += 1;
    const readId = readIdRef.current;
    if (tenantId === undefined) {
      setHeld({ tenantId, status: 'idle', urls: [] });
      return undefined;
    }
    const abort = new AbortController();
    setHeld((prev) =>
      prev.tenantId === tenantId && prev.status === 'ready' ? prev : { tenantId, status: 'loading', urls: [] },
    );
    void fetchSiteArt(tenantId, abort.signal).then((read) => {
      if (readId !== readIdRef.current || (!read.ok && read.aborted)) return;
      setHeld((prev) => {
        if (read.ok) return { tenantId, status: 'ready', urls: siteArtImageUrls(read.value) };
        // A failed re-read keeps the last good list; only a first read shows the failure.
        return prev.tenantId === tenantId && prev.status === 'ready' ? prev : { tenantId, status: 'failed', urls: [] };
      });
    });
    return () => abort.abort();
  }, [tenantId, nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  // A tenant change shows the new tenant's first read, never the previous tenant's list.
  if (held.tenantId !== tenantId) return { status: tenantId === undefined ? 'idle' : 'loading', urls: [], reload };
  return { status: held.status, urls: held.urls, reload };
};

export default useSiteArtLibrary;
